/* eslint max-lines: ["error", {max: 335}] -- Long file due to the complexity of the core component */
/* eslint complexity: ["error", {max: 15}] -- this overrides for the entire file */
import { useCallback, useState } from "react";

import type { ButtonProps as BaseButtonProps } from "tamagui";
import { Button as BaseButton } from "tamagui";

import {
  AccentBlueStyles,
  AccentCriticalStyles,
  AccentLimeStyles,
  DisabledContentColor,
  PrimaryOnBackgroundStyles,
  PrimaryStyles,
  SecondaryStyles,
  TertiaryCriticalStyles,
  TertiaryStyles,
  ToggleableStyles,
} from "@/ui/core/buttons/styles";
import type { FAIconName, IconSize } from "@/ui/core/Icon";
import Icon from "@/ui/core/Icon";
import Spinner from "@/ui/core/Spinner";
import Text from "@/ui/core/Text";
import Box from "@/ui/layout/Box";
import { HStack } from "@/ui/layout/Stacks";
import { ShadowStyles } from "@/ui/theme/styles";
import { ColorToken, OpacityToken } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

type ButtonVariant =
  | "accent.blue"
  | "accent.critical"
  | "accent.lime"
  | "primary"
  | "primary.on_background"
  | "secondary"
  | "tertiary"
  | "tertiary.critical"
  | "toggleable";

type ButtonSize = "normal" | "small";

const ButtonSizeToIconSizeMap: Record<ButtonSize, IconSize> = {
  normal: "md",
  small: "sm",
};

interface ButtonProps extends BaseViewProps {
  isActive?: boolean;
  isDisabled?: boolean;
  isFAB?: boolean;
  isFullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: FAIconName;
  onPress: () => Promise<void> | void;
  rightIcon?: FAIconName;
  size?: ButtonSize;
  title: string;
  variant?: ButtonVariant;
}

interface ButtonContentProps extends ViewWithTestID {
  contentColor: ColorToken;
  isDisabled?: boolean;
  isLoading?: boolean;
  leftIcon?: FAIconName;
  rightIcon?: FAIconName;
  shouldUnderlineOnPress: boolean;
  size: ButtonSize;
  title: string;
}

const ButtonStyleMapper: Record<ButtonVariant, BaseButtonProps> = {
  "accent.blue": AccentBlueStyles,
  "accent.critical": AccentCriticalStyles,
  "accent.lime": AccentLimeStyles,
  primary: PrimaryStyles,
  "primary.on_background": PrimaryOnBackgroundStyles,
  secondary: SecondaryStyles,
  tertiary: TertiaryStyles,
  "tertiary.critical": TertiaryCriticalStyles,
  toggleable: ToggleableStyles,
};

const ContentColorMapper: Record<ButtonVariant, ColorToken> = {
  "accent.blue": ColorToken.NeutralBackgroundPrimary,
  "accent.critical": ColorToken.NeutralBackgroundPrimary,
  "accent.lime": ColorToken.Primary90Variant,
  primary: ColorToken.NeutralBackgroundPrimary,
  "primary.on_background": ColorToken.NeutralBackgroundPrimary,
  secondary: ColorToken.Primary100,
  tertiary: ColorToken.Primary100,
  "tertiary.critical": ColorToken.NotificationCritical,
  toggleable: ColorToken.NeutralPrimary,
};

const getDisabledContentColor = (variant: ButtonVariant): ColorToken => {
  if (variant === "primary.on_background") {
    return ContentColorMapper["primary.on_background"];
  }
  return DisabledContentColor;
};

const getActiveContentColor = (variant: ButtonVariant): ColorToken => {
  if (variant === "toggleable") {
    return ColorToken.Primary100;
  }
  return ContentColorMapper[variant];
};

const getContentColor = ({
  isActive,
  isDisabled,
  variant,
}: {
  isActive?: boolean;
  isDisabled?: boolean;
  variant: ButtonVariant;
}): ColorToken => {
  if (isDisabled) {
    return getDisabledContentColor(variant);
  }

  if (isActive) {
    return getActiveContentColor(variant);
  }

  return ContentColorMapper[variant];
};

const getButtonHeight = ({
  size,
  variant,
}: {
  size: ButtonSize;
  variant: ButtonVariant;
  // eslint-disable-next-line sonarjs/function-return-type -- currently reported in SonarQube as unresolved
}): number | string => {
  if (variant === "tertiary") {
    return "auto";
  }
  return size === "small" ? 32 : 40;
};

interface GetHorizontalPaddingArgs {
  icon?: FAIconName;
  isFAB?: boolean;
  variant?: ButtonVariant;
}

const getHorizontalPadding = ({
  icon,
  isFAB,
  variant,
}: GetHorizontalPaddingArgs) => {
  if (variant === "tertiary") {
    return 0;
  }

  if (isFAB) {
    return 24;
  }

  if (variant === "toggleable") {
    return 18;
  }

  return icon ? 14 : 16;
};

const ButtonContent = ({
  contentColor,
  isDisabled,
  isLoading,
  leftIcon,
  rightIcon,
  shouldUnderlineOnPress,
  size,
  testID,
  title,
}: ButtonContentProps) => {
  const iconSize = ButtonSizeToIconSizeMap[size];
  const LeftIcon = leftIcon && (
    <Icon
      color={contentColor}
      name={leftIcon}
      opacity={isLoading ? OpacityToken.Hidden : OpacityToken.Visible}
      size={iconSize}
      testID={joinTestIDs(testID, "left")}
    />
  );
  const RightIcon = rightIcon && (
    <Icon
      color={contentColor}
      name={rightIcon}
      opacity={isLoading ? OpacityToken.Hidden : OpacityToken.Visible}
      size={iconSize}
      testID={joinTestIDs(testID, "right")}
    />
  );

  return (
    <HStack position="relative">
      <HStack alignItems="center" gap={6}>
        {LeftIcon}
        <Text
          color={contentColor}
          isDisabled={isDisabled}
          opacity={isLoading ? OpacityToken.Hidden : OpacityToken.Visible}
          testID={joinTestIDs(testID, "button", "text")}
          textDecorationLine={shouldUnderlineOnPress ? "underline" : "none"}
          variant={size === "small" ? "captionMedium" : "bodyMedium"}
        >
          {title}
        </Text>
        {RightIcon}
      </HStack>
      <Box
        bottom={0}
        left={0}
        opacity={isLoading ? OpacityToken.Visible : OpacityToken.Hidden}
        position="absolute"
        right={0}
        top={0}
      >
        <Spinner color={contentColor} size="small" />
      </Box>
    </HStack>
  );
};

const Button = ({
  accessibilityLabel,
  isActive,
  isDisabled,
  isFAB,
  isFullWidth,
  isLoading,
  leftIcon,
  onPress,
  rightIcon,
  size = "normal",
  testID,
  title,
  variant = "primary",
}: ButtonProps) => {
  const contentColor = getContentColor({
    isActive,
    isDisabled,
    variant,
  });

  const handlePress = useCallback(
    (event?: { stopPropagation?: () => void }) => {
      // on web, this prevents the parent component from also receiving this press
      // on native, this works as intended (only this button receives the press)
      if (isWeb()) event?.stopPropagation?.();
      if (!isDisabled && !isLoading) {
        void onPress();
      }
    },
    [isDisabled, isLoading, onPress],
  );

  const [isPressed, setIsPressed] = useState<boolean>(false);
  const isTertiaryStyle =
    variant === "tertiary" || variant === "tertiary.critical";
  const shouldUnderlineOnPress = isPressed && isTertiaryStyle;
  const shouldShowShadow = isFAB;

  const disabledStyle = isDisabled
    ? ButtonStyleMapper[variant].disabledStyle
    : undefined;
  const baseStyles = {
    ...ButtonStyleMapper[variant],
    disabledStyle,
  };
  const activeStyles = {
    backgroundColor: baseStyles.pressStyle?.backgroundColor,
    borderColor: baseStyles.pressStyle?.borderColor,
  };

  const buttonHeight = getButtonHeight({ size, variant });
  return (
    <Box
      aria-disabled={isDisabled}
      disabled={isDisabled}
      flexDirection="row"
      flexShrink={isFullWidth ? 1 : 0}
      // flexShrink:1 lets a full-width button shrink horizontally to share a row
      // on web, when in a column RN-web's default min-height:0 means it collapses vertically to 0 (eg, Edit Appointment Series button on scroll)
      minHeight={isWeb() && isFullWidth ? buttonHeight : undefined}
    >
      <BaseButton
        aria-disabled={isDisabled}
        aria-label={accessibilityLabel}
        aria-selected={isActive}
        borderRadius={20}
        disabled={isDisabled}
        gap={0}
        height={buttonHeight}
        onPress={handlePress}
        onPressIn={() => setIsPressed(true)}
        onPressOut={() => setIsPressed(false)}
        paddingLeft={getHorizontalPadding({ icon: leftIcon, isFAB, variant })}
        paddingRight={getHorizontalPadding({ icon: rightIcon, isFAB, variant })}
        testID={joinTestIDs(testID, "button")}
        width={isFullWidth ? "100%" : "auto"}
        // eslint-disable-next-line react/jsx-props-no-spreading, @typescript-eslint/no-explicit-any,  @typescript-eslint/consistent-type-assertions
        {...(baseStyles as any)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(shouldShowShadow && ShadowStyles)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(isActive && activeStyles)}
      >
        <ButtonContent
          contentColor={contentColor}
          isDisabled={isDisabled}
          isLoading={isLoading}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          shouldUnderlineOnPress={shouldUnderlineOnPress}
          size={size}
          testID={testID}
          title={title}
        />
      </BaseButton>
    </Box>
  );
};

export default Button;

export type { ButtonProps, ButtonSize, ButtonVariant };
